import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useRouter, useRouterState } from "@tanstack/react-router";

import { setRenderLang } from "@/lib/pt-localize";
import { contentLang, type Lang, type SiteLang } from "@/lib/lang-url";

export type { Lang, SiteLang };

export const languages: { code: SiteLang; label: string }[] = [
  { code: "en", label: "EN" },
  { code: "pt", label: "PT" },
  { code: "es", label: "ES" },
];

const dict = {
  works: { en: "Works", pt: "Trabalhos", es: "Trabajos" },

  about: { en: "About", pt: "Sobre", es: "Sobre" },
  clipping: { en: "Clipping", pt: "Clipping", es: "Prensa" },
  contact: { en: "Contacts", pt: "Contato", es: "Contacto" },
  scrollIndex: { en: "Scroll — index", pt: "Role — índice", es: "Desliza — índice" },
  pickJourney: { en: "Pick your journey", pt: "Escolha seu percurso", es: "Elige tu recorrido" },
  mainProjects: { en: "Main projects", pt: "Principais projetos", es: "Proyectos principales" },
  selectedWorks: { en: "Selected works", pt: "Trabalhos selecionados", es: "Trabajos seleccionados" },
  allProjects: { en: "All projects", pt: "Todos os projetos", es: "Todos los proyectos" },
  entries: { en: "entries", pt: "entradas", es: "entradas" },
  practice: {
    en: "Transdisciplinary practice",
    pt: "Prática transdisciplinar",
    es: "Práctica transdisciplinar",
  },
  memoryMaterial: {
    en: "Memory as material — the body as laboratory.",
    pt: "A memória como matéria — o corpo como laboratório.",
    es: "La memoria como materia — el cuerpo como laboratorio.",
  },
  statement: {
    en: "* Expanded photography, noise, ritual performance and neuroscience — images that decode what memory refuses to keep.",
    pt: "* Fotografia expandida, ruído, performance ritual e neurociência — imagens que decodificam o que a memória se recusa a guardar.",
    es: "* Fotografía expandida, ruido, performance ritual y neurociencia — imágenes que decodifican lo que la memoria se niega a guardar.",
  },
  nextProject: { en: "Next project", pt: "Próximo projeto", es: "Próximo proyecto" },
  index: { en: "Index", pt: "Índice", es: "Índice" },
  artistResearcher: {
    en: "Artist · Researcher · Scientist",
    pt: "Artista · Pesquisadora · Cientista",
    es: "Artista · Investigadora · Científica",
  },
} as const;

export type TKey = keyof typeof dict;

const I18nContext = createContext<{ lang: Lang; siteLang: SiteLang; setLang: (l: SiteLang) => void }>({
  lang: "en",
  siteLang: "en",
  setLang: () => {},
});

export function I18nProvider({ children }: { children: ReactNode }) {
  const router = useRouter();
  // The address decides the language, so the server renders the page in it
  // and a shared link always opens in the same one.
  const [siteLang, setLangState] = useState<SiteLang>(() => router.$lang.get());
  setRenderLang(siteLang);
  // Catalog-only languages read the English copy, then the catalog translates it.
  const lang = contentLang(siteLang);

  const setLang = useCallback(
    (next: SiteLang) => {
      if (next === router.$lang.get()) return;
      router.$lang.set(next);
      setLangState(next);
      // Re-write the current address: /works becomes /pt/works.
      void router.navigate({ href: router.latestLocation.href });
    },
    [router],
  );

  // Back and forward cross language prefixes, so follow the address.
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  useEffect(() => {
    setLangState(router.$lang.get());
  }, [pathname, router]);

  useEffect(() => {
    document.documentElement.lang = siteLang;
  }, [siteLang]);

  const value = useMemo(() => ({ lang, siteLang, setLang }), [lang, siteLang, setLang]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const { lang, siteLang, setLang } = useContext(I18nContext);
  return {
    lang,
    siteLang,
    setLang,
    t: (key: TKey) => dict[key][lang],
    tx: (l: { en: string; pt: string; es: string }) => l[lang],
  };
}
